import React, { useEffect, useState } from "react";
import { connect } from 'react-redux';
import { useHistory, Link, Redirect } from "react-router-dom";
import { Resizable } from 're-resizable'
import moment from 'moment';
import ShippingInformationList from '@features/shippinginformation/ShippingInformationList';
import { locationSelectorActions } from "@features/location/selector/state_management/actions";
import { overlappingActions } from "@features/setting/overlapping/state_management/actions";
import ShipmentOrderDetail from '@features/main/HomeComponent/ShipmentOrderDetail';
import ProcessChart from '@features/main/HomeComponent/ProcessChart';
import { RootState, AppState, LocationSelectorState, Dispatcher, ShippingInformationState, ShipmentState, AuthState } from "AppTypes";
import { api } from "@services"
import { success, error } from '@services/toast-service'
import ThreeViewer from './HomeComponent/3dViewer';
import { useDisplayMode } from '@utils/useDisplayMode';
import { getSelectLocationRoute } from '@utils/func';
import Overlay2D from '../../components/Overlay2D';
import { STATUS_MESH } from '../../const/constants';
import ListMesh from './HomeComponent/ListMesh'
import LoadingSpinner from "../../components/LoadingSpinner";
import { shippingInformationActions } from "@features/shippinginformation/state_management/actions";
import { authActions } from '../auth/state_management/actions';

type Props = {
    dispatch: Dispatcher;
    appState: AppState;
    locationSelectorState: LocationSelectorState;
    shippingInformationState: ShippingInformationState;
    shipmentState: ShipmentState;
    authState: AuthState;
};

const MainPage: React.FC<Props> = ({
    appState,
    locationSelectorState,
    dispatch,
    shippingInformationState,
    shipmentState,
    authState
}) => {
    const { dashboardChartExpanded, dashboardShipmentInfoExpaned } = appState;
    const { is2D, loaded } = useDisplayMode(); // #8428: 2D disables 再生 + shows PNG overlay
    const history = useHistory();
    const locationId = localStorage.getItem('currentLocationId') || appState.currentLocationId || '';
    const [isLoading, setIsLoading] = useState<boolean>(false);
    const { orderByLocation } = shipmentState;
    const { code, plannedDate } = orderByLocation;

    useEffect(() => {
        // #8427/SKY-28: in 2D the 事務所画面 (this screen) is the Office landing and does NOT
        // require a pre-selected 打設場所 — show it empty and let the user pick from 出荷登録.
        // Only 3D bounces a location-less Office to 打設場所選択. Gated on `loaded` so the
        // redirect never fires while the mode is still uncertain (would loop).
        if (loaded && !locationId && !is2D) {
            history.push(getSelectLocationRoute(is2D));
        }
        dispatch(authActions.fetchRedirectConfig());
    }, [appState.currentLocationId, is2D, loaded]);

    // #8428: in 2D the 事務所 overlay needs mesh statuses to colour its cells (read-only view).
    useEffect(() => {
        if (is2D && locationId) {
            dispatch(locationSelectorActions.fetchLocationMesh({ locationId, igoreFilter: true }));
            // SKY: the 打重ね ポンプ車 dropdown reads shippingInformation.pumpList, which in 3D is
            //   filled by <ThreeViewer> (3dViewer.tsx). 2D renders <Overlay2D> instead, so nothing
            //   loaded it and the dropdown showed only 「全体」 until another screen happened to
            //   fetch it. Load it here so the list is complete on first display.
            dispatch(shippingInformationActions.fetchPumpList({ LocationId: locationId }));
        }
    }, [is2D, locationId]);

    const { loginRedirectConfig } = authState;

    // #8428 / 事務所_詳細②-1: flattened mesh cells for the 2D overlay. `pumpMismatch` greys
    // 未打設 cells of another pump while a specific ポンプ車 is selected (「全体」 = no grey);
    // the orange selection mirrors the 3D office behaviour (changeMeshActiveListMesh) so the
    // linked 打重ね情報一覧/運搬情報一覧 rows turn red exactly like a 3D mesh click.
    const meshes2D = is2D
        ? (locationSelectorState.locationMesh || []).reduce((acc: any[], loc: any) => acc.concat(loc.meshes || []), [])
        : [];
    // SKY-131: every selected mesh, as the 3D office view highlights them (3dViewer isMeshActive).
    // Tapping a 運搬情報一覧 row selects all the meshes that truck poured (meshCodes); `find`
    // kept only the first, so e.g. 1-5 stayed unhighlighted next to 1-1, and a first mesh on a
    // layer not shown left nothing orange at all.
    const selectedMeshes2D = is2D
        ? meshes2D.filter((m: any) => locationSelectorState.listMeshActiveTable?.includes(m.code))
        : [];
    const handleCellClick2D = (cell: { layer: number; row: number; meshId?: string }) => {
        const mesh = meshes2D.find((m: any) => (cell.meshId && m.id === cell.meshId) || (m.layer === cell.layer && m.row === cell.row));
        if (!mesh?.code) return;
        // same toggle as the 3D office mesh click (3dViewer.setActiveMeshToTable)
        const willSelect = !locationSelectorState.listMeshActiveTable?.includes(mesh.code);
        dispatch(locationSelectorActions.changeMeshActiveListMesh(mesh.code));
        dispatch(shippingInformationActions.setShippingFromMesh({
            type: willSelect ? 1 : 2,
            meshCode: mesh.code,
        }));
    };

    //reports
    // SKY-169: in 2D the 事務所画面 may be shown with no 打設場所 selected (SKY-28), but the
    // 試験登録 / 帳表 / データ修正 entries all work on the current location: 試験登録 opened a
    // blank screen and the downloads failed silently (the BE rejects an empty locationId).
    // Refuse them up front with the message 出荷登録 already uses.
    const requireLocation = (): boolean => {
        if (locationId) return true;
        error('打設場所を選択してください。');
        return false;
    }
    const guardLocationLink = (e: React.MouseEvent) => {
        if (!requireLocation()) e.preventDefault();
    }
    // SKY-169: downloads use responseType 'blob', so the BE's ApiResult error arrives as a Blob.
    const readReportError = async (err: any): Promise<{ statusCode?: number; message?: string }> => {
        try {
            const body = err?.response?.data;
            const text = body instanceof Blob ? await body.text() : body;
            const rs = typeof text === 'string' ? JSON.parse(text) : text;
            return { statusCode: rs?.statusCode, message: rs?.errors?.[0] };
        } catch {
            return {};
        }
    }
    // SKY-169: for a location with no 出荷登録 the report handlers answer 「出荷登録をしてください。」,
    // and 打設管理表（車両毎）with no truck at 荷卸終了 answers 「運搬データがありません。」, as an
    // AppException (statusCode 400) carrying the message to show. A BE predating this answers
    // both with DataNotFound (404): keep the 出荷登録 message for it. Other failures stay silent.
    const showReportError = async (err: any) => {
        const { statusCode, message } = await readReportError(err);
        if (statusCode === 400 && message) {
            error(message);
        } else if (statusCode === 404) {
            error('出荷登録をしてください。');
        }
    }

    const handleDownloadCastingRecordReport = async () => {
        if (!requireLocation()) return;
        setIsLoading(true);

        try {
            const fileBlob = await api.locationAPI.exportCastingRecordReport(locationId);
            const fileUrl = window.URL.createObjectURL(fileBlob)
            const link = document.createElement('a')
            link.href = fileUrl
            link.download = `${moment(plannedDate).format('YYYYMMDD')}_打設記録表.xlsx`
            document.body.appendChild(link)
            link.click()
            success('ダウンロードが完了しました')
            document.body.removeChild(link)
            setIsLoading(false);
        } catch (err) {
            setIsLoading(false);
            await showReportError(err);
        }
    }

    const handleDownloadCastingManagementReport = async () => {
        if (!requireLocation()) return;
        setIsLoading(true);
        try {
            const fileBlob = await api.locationAPI.exportCastingManagementReport(locationId);
            const fileUrl = window.URL.createObjectURL(fileBlob)
            const link = document.createElement('a')
            link.href = fileUrl
            link.download = `${moment(plannedDate).format('YYYYMMDD')}_打設管理表.xlsx`
            document.body.appendChild(link)
            link.click()
            success('ダウンロードが完了しました')
            document.body.removeChild(link)
            setIsLoading(false);

        } catch (err) {
            setIsLoading(false);
            await showReportError(err);
        }
    }

    const handleDownloadCastingTimeReport = async () => {
        if (!requireLocation()) return;
        setIsLoading(true);
        try {
            const fileBlob = await api.locationAPI.exportCastingTimeReport(locationId);
            const fileUrl = window.URL.createObjectURL(fileBlob)
            const link = document.createElement('a')
            link.href = fileUrl
            const fileName = plannedDate ? `${moment(plannedDate).format('YYYYMMDD')}_打重ね時間管理表.xlsx` : `打重ね時間管理表.xlsx`
            link.download = fileName
            document.body.appendChild(link)
            link.click()
            success('ダウンロードが完了しました')
            document.body.removeChild(link)
            setIsLoading(false);
        } catch {
            setIsLoading(false);
        }
    }

    const handleDownloadCastingDetailReport = async () => {
        if (!requireLocation()) return;
        setIsLoading(true);
        try {
            const fileBlob = await api.locationAPI.exportCastingDetailReport(locationId);
            const fileUrl = window.URL.createObjectURL(fileBlob)
            const link = document.createElement('a')
            link.href = fileUrl
            link.download = `${moment(plannedDate).format('YYYYMMDD')}_打設詳細表.xlsx`
            document.body.appendChild(link)
            link.click()
            success('ダウンロードが完了しました')
            document.body.removeChild(link)
            setIsLoading(false);
        } catch (err) {
            setIsLoading(false);
            await showReportError(err);
        }
    }
    const handleDownloadShipmentReport = async () => {
        setIsLoading(true);
        try {
            const fileBlob = await api.shippingInformationAPI.exportShipmentReport();
            const fileUrl = window.URL.createObjectURL(fileBlob)
            const link = document.createElement('a')
            link.href = fileUrl
            link.download = `${moment(new Date()).format('YYYYMMDD')}_打設情報一覧.xlsx`
            document.body.appendChild(link)
            link.click()
            success('ダウンロードが完了しました')
            document.body.removeChild(link)
            setIsLoading(false);

        } catch {
            setIsLoading(false);
        }
    }

    return (
        <div className="App home-office px-1">
            <LoadingSpinner isShowing={isLoading} />
            {!locationSelectorState.isFull3dMode && (
                <div className="col-12 row">
                    <nav className="ml-2 d-flex">
                        <div className="dropdown main-dropdown-menu">
                            <button className="border-end-0">
                                <Link
                                    className="shipment"
                                    to="/shipment/registration"
                                >
                                    出荷登録
                                </Link>
                            </button>
                            <div className="menu-main">
                                <button className="border-end-0">
                                    <a
                                        className="setting"
                                        onClick={() => {}}
                                        aria-hidden="true"
                                    >
                                        試験登録
                                    </a>
                                </button>
                                <ul>
                                    <li className="border-bottom-0">
                                        <Link
                                            className=""
                                            to="/setting/slumpinfo"
                                            onClick={guardLocationLink}
                                        >
                                            スランプ
                                        </Link>
                                    </li>
                                    <li>
                                        <Link
                                            className=""
                                            to="/setting/compressionexperiment"
                                            onClick={guardLocationLink}
                                        >
                                            圧縮強度
                                        </Link>
                                    </li>
                                </ul>
                            </div>
                            {/* #8428: disable 再生 (playback) in 2D mode — black text, gray bg, non-clickable */}
                            {is2D ? (
                                <button className="border-end-0" disabled aria-disabled="true"
                                    style={{ background: '#c9c9c9', cursor: 'not-allowed' }}>
                                    <span style={{ color: '#000' }}>再生</span>
                                </button>
                            ) : (
                            <button className="border-end-0">
                                <Link
                                    className=""
                                    to="/replay-location-history"
                                >
                                    再生
                                </Link>
                            </button>
                            )}
                            <div className="menu-main">
                                <button className="border-end-0">
                                    <a
                                        className="setting"
                                        onClick={() => {}}
                                        aria-hidden="true"
                                    >
                                        帳表
                                    </a>
                                </button>
                                <ul>
                                    <li className="border-bottom-0">
                                        <a
                                            aria-hidden="true"
                                            onClick={handleDownloadCastingRecordReport}
                                        >
                                            コンクリート試験記録
                                        </a>
                                    </li>
                                    <li className="border-bottom-0">
                                        <a
                                            aria-hidden="true"
                                            onClick={handleDownloadCastingManagementReport}
                                        >
                                            打設管理表（車両毎の運搬時間及び試験記録）
                                        </a>
                                    </li>
                                    <li className="border-bottom-0">
                                        <a
                                            aria-hidden="true"
                                            onClick={handleDownloadCastingTimeReport}
                                        >
                                            打重ね時間管理表
                                        </a>
                                    </li>
                                    <li>
                                        <a
                                            aria-hidden="true"
                                            onClick={handleDownloadCastingDetailReport}
                                        >
                                            打設管理表（打設メッシュ毎の運搬時間及び試験記録）
                                        </a>
                                    </li>
                                </ul>
                            </div>
                            {process.env.REACT_APP_MODE_RUN !== 'viewer' && (
                                <div className="menu-main">
                                    <button className="">
                                        <a
                                            className="setting"
                                            onClick={() => { }}
                                            aria-hidden="true"
                                        >
                                            データ修正
                                        </a>
                                    </button>
                                    <ul>
                                        <li className="border-bottom-0">
                                            <Link
                                                className=""
                                                to="/shippinginformationeditor"
                                                onClick={guardLocationLink}
                                            >
                                                運搬情報編集
                                            </Link>
                                        </li>
                                        <li>
                                            <Link
                                                className=""
                                                to="/setting/staking"
                                                onClick={guardLocationLink}
                                            >
                                                打重ね情報編集
                                            </Link>
                                        </li>
                                    </ul>
                                </div>
                            )}
                            　
                            {(<a href={loginRedirectConfig.deviceStatusUrl + "?DasetsukasyoCd=" + code} target="_blank">機器情報</a>)}

                            {shipmentState?.processChartData?.isCompression && (<Link to="/setting/compressionexperiment" className="compression-link ml-5 text-bold" >本日圧縮強度試験の登録が必要です</Link>)}　
                        </div>
                    </nav>
                </div>
            )}
            
            <div className="col-12 row">
                {!locationSelectorState.isFull3dMode && (
                    <div className={dashboardShipmentInfoExpaned ? "col-6 p-0" : "col-3 p-0"} hidden={dashboardChartExpanded}>
                        <ShipmentOrderDetail />
                    </div>
                )}
                {!locationSelectorState.isFull3dMode && (
                    <div className={dashboardChartExpanded ? "col-6 p-0" : "col-3 p-0"} hidden={dashboardShipmentInfoExpaned}>
                        <ProcessChart />
                    </div>
                )}
                <div className={locationSelectorState.isFull3dMode ? 'col-12 p-0 view-3d-full' : 'col-6 p-0 view-3d-content'}
                    style={is2D ? { display: 'flex', flexDirection: 'column' } : undefined}>
                    <div className="header-three-view bg-warning col-12 p-0 d-flex">
                        <div className="text-center col-9 p-0 fw-bold text-cursor" aria-hidden="true" onClick={() => dispatch(locationSelectorActions.changeModeShow3DView(!locationSelectorState.isFull3dMode))}>打重ね</div>
                        <div className="col-3 p-0">
                            <select className="form-select form-select-sm" value={locationSelectorState.pumpSelect} onChange={event => {
                                dispatch(locationSelectorActions.changePumpSelect(event.target.value));
                                // dispatch(locationSelectorActions.fetchLocationMesh({ locationId: locationSelectorState.locationSelect, ignoreFilter: false }));
                                dispatch(overlappingActions.fetchList({ LocationId: locationId, PumpId: event.target.value }));
                                dispatch(shippingInformationActions.changePumpSelect(event.target.value));
                            }}>
                                <option value="">全体</option>
                                {shippingInformationState.pumpList?.map(itemPump => (
                                    <option key={itemPump.id} value={itemPump.id}>{itemPump.name}</option>
                                ))}
                            </select>
                        </div>
                    </div>
                    {/* #8428: in 2D show the PNG overlay (office view) instead of the 3D model */}
                    {is2D ? (
                        <Overlay2D
                            locationId={locationId}
                            fullScreen={locationSelectorState.isFull3dMode}
                            meshList={meshes2D.map((m: any) => ({
                                id: m.id,
                                layer: m.layer,
                                row: m.row,
                                status: typeof m.status === 'number' ? m.status : STATUS_MESH.indexOf(m.status),
                                warningStatus: m.warningStatus,
                                pumpId: m.pumpId,
                                // 事務所②-1 ⑤: 未打設 + a different ポンプ車 selected -> grey
                                // SKY-162: a cell with no ポンプ車 assigned (PumpId NULL) belongs to
                                // no pump, so it must not be greyed as "another pump's cell".
                                pumpMismatch: !!locationSelectorState.pumpSelect
                                    && !!m.pumpId
                                    && m.pumpId !== locationSelectorState.pumpSelect,
                                // SKY-173: the customer wants another pump's 打設中 / 打設済 cell gray
                                // here too (as on 現場画面, SKY-172); 警告 / 制限 still show.
                                grayAnyStatus: true,
                            }))}
                            onCellClick={handleCellClick2D}
                            selectedCells={selectedMeshes2D.map((m: any) => ({ layer: m.layer, row: m.row }))}
                        />
                    ) : (
                        <ThreeViewer />
                    )}
                </div>
            </div>
            {!locationSelectorState.isFull3dMode && (
                <div className="d-flex">
                    <Resizable
                        style={{ width: '100%', margin: 0, boxSizing: 'border-box' }}
                        defaultSize={{
                            width: '50%',
                            height: '100%',
                        }}
                        maxWidth="100%"
                        minWidth="1"
                    >
                        <ShippingInformationList />
                    </Resizable>
                    <div style={{ width: '100%', height: '100%', minWidth: '1px', margin: 0, boxSizing: 'border-box' }}><ListMesh /></div>
                </div>
            )}
        </div>
    )
};
export default connect((state: RootState) => ({
    appState: state.app,
    locationSelectorState: state.locationSelector,
    shippingInformationState: state.shippingInformation,
    shipmentState: state.shipment,
    authState: state.auth,
}))(MainPage);
